import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { TRAILERS } from "@/lib/site";

export default function Trailers() {
  const [active, setActive] = useState(null);

  return (
    <section id="trailers" data-testid="trailers-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          overline="Our Trailers"
          title={
            <>
              A fleet dressed in <span className="italic bronze-text">white &amp; bronze</span>
            </>
          }
          description="Every trailer arrives detailed, staged, and ready for guests. Choose the suite that fits your occasion."
        />
        <div className="mt-20 space-y-24 lg:space-y-32">
          {TRAILERS.map((t, i) => (
            <Reveal key={t.id}>
              <article
                data-testid={`trailer-card-${t.id}`}
                className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-16 ${i % 2 === 1 ? "" : ""}`}
              >
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="gallery-frame group">
                    <div className="relative overflow-hidden">
                      <img
                        src={t.exterior}
                        alt={`${t.name} — ${t.tag} exterior`}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        data-testid={`trailer-image-${t.id}`}
                      />
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:text-right" : ""}`}>
                  <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-bronze-dark">{t.tag}</p>
                  <h3 className="mt-3 font-serif text-3xl font-light text-stone-900 sm:text-4xl">{t.name}</h3>
                  <p className="mt-5 text-base font-light leading-relaxed text-stone-600">{t.blurb}</p>
                  <p className={`mt-5 text-sm text-stone-500 ${i % 2 === 1 ? "lg:justify-end" : ""} flex items-center gap-2`}>
                    <Check className="h-4 w-4 text-brand-bronze-dark" aria-hidden="true" />
                    {t.bestFor}
                  </p>
                  <div className={`mt-8 flex flex-wrap gap-4 ${i % 2 === 1 ? "lg:justify-end" : ""}`}>
                    <button
                      data-testid={`trailer-details-button-${t.id}`}
                      onClick={() => setActive(t)}
                      className="border border-stone-300 px-6 py-3 text-sm tracking-wide text-stone-900 transition-colors duration-300 hover:border-brand-bronze"
                    >
                      View Details
                    </button>
                    <Link
                      to={`/book?trailer=${t.id}`}
                      data-testid={`trailer-book-button-${t.id}`}
                      className="group inline-flex items-center gap-2 bg-stone-900 px-6 py-3 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900"
                    >
                      Book This Trailer
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16">
          <p className="text-xs tracking-wide text-stone-400" data-testid="fleet-disclaimer">
            Trailer line-up shown is representative of the LooXury fleet. Availability varies by date — we'll confirm
            the perfect fit when you request a quote.
          </p>
        </Reveal>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="trailer-detail-modal" className="max-w-3xl rounded-none border-brand-bronze/20 bg-stone-50 p-0">
          {active && (
            <div className="grid md:grid-cols-2">
              <div className="overflow-hidden">
                <img src={active.interior} alt={`${active.name} interior`} className="h-full w-full object-cover" data-testid="trailer-detail-image" />
              </div>
              <div className="p-8">
                <DialogHeader>
                  <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-bronze-dark">{active.tag}</p>
                  <DialogTitle className="mt-2 font-serif text-3xl font-light text-stone-900">{active.name}</DialogTitle>
                </DialogHeader>
                <p className="mt-4 text-sm font-light leading-relaxed text-stone-600">{active.blurb}</p>
                <ul className="mt-6 space-y-3">
                  {active.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-stone-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-bronze-dark" aria-hidden="true" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book?trailer=${active.id}`}
                  data-testid="trailer-detail-book-button"
                  className="mt-8 inline-block bg-stone-900 px-6 py-3 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900"
                >
                  Book {active.name}
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
